import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Row, RowItemType, Section, SectionType } from '../../../models';
import {
    InstallationTypeSectionIds,
    ChannelInstallationFieldIds,
    ChannelInstallationMp2FieldIds,
} from '../../common.models';
import { setArraySectionItems, setFieldValue } from '../../../actions';
import { LocationDetails } from 'app/shared/models/location-details';
import { Entries } from 'app/shared/models/add-location-channel';
import { field } from '../../../core/field-schema';
import { required, maxLength, pattern, min, max } from '../../../core/validator-builders';
import { UnknownLocationCardState } from '../models';

export const getValidators = function (isMetric: boolean) {
    const validators = [required(), maxLength({ value: 100 })];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.installationMetricValuePattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.installationValuePattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

export const channelRowGenerator =
    (state: UnknownLocationCardState, isMp1: boolean) =>
    (index: number, toPopulate?: Entries): Row => {
        const settings = state.settings;

        const FieldIds = isMp1 ? ChannelInstallationFieldIds : ChannelInstallationMp2FieldIds;
        return {
            info: {
                id: 'channel-entry-row',
                panelClass: 'channel-entry-row custom-50p',
            },
            items: [
                field.number(`${FieldIds.Depth}-${index}`, 'depth', {
                    value: toPopulate ? toPopulate.depth : null,
                    placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.DEPTH',
                    placeholderSuffix: settings.depthUnit,
                    validators: settings.isMetric
                        ? [
                              required(),
                              maxLength({ value: 10 }),
                              pattern({
                                  value: REGEX_CONFIG.numeric0_10000With1DecimalPlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR',
                              }),
                          ]
                        : [
                              required(),
                              maxLength({ value: 10 }),
                              max({ value: 393, message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_DEPTH_PATTERN_ERROR' }),
                              pattern({
                                  value: REGEX_CONFIG.numericDecimal2PlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_DEPTH_PATTERN_ERROR',
                              }),
                          ],
                    panelClass: 'pad-t-6 required',
                    events: {
                        onChange: (value) => {
                            const formatted = Number(Number(value).toFixed(settings.isMetric ? 1 : 2));
                            return isNaN(formatted) || !value
                                ? []
                                : [setFieldValue({ fieldId: `${FieldIds.Depth}-${index}`, value: formatted })];
                        },
                    },
                }),
                field.number(`${FieldIds.Quantity}-${index}`, 'quantity', {
                    value: toPopulate ? toPopulate.quantity : null,
                    placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.QUANTITY',
                    validators: settings.isMetric
                        ? [
                              required(),
                              maxLength({ value: 10 }),
                              pattern({
                                  value: REGEX_CONFIG.numeric0_10000With1DecimalPlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR',
                              }),
                              max({ value: 10000, message: 'HOME.MAP.MARKER_LOCATION_DETAIL.ONE_PRECISION_ERROR' }),
                          ]
                        : [
                              required(),
                              maxLength({ value: 10 }),
                              max({ value: 1000, message: 'HOME.MAP.MARKER_LOCATION_DETAIL.THREE_PRECISION_ERROR' }),
                              pattern({
                                  value: REGEX_CONFIG.numericDecimal3PlacePattern,
                                  message: 'HOME.MAP.MARKER_LOCATION_DETAIL.THREE_PRECISION_ERROR',
                              }),
                          ],
                    panelClass: 'pad-t-6 required',
                    events: {
                        onChange: (value) => {
                            const formatted = Number(Number(value).toFixed(settings.isMetric ? 1 : 3));
                            return isNaN(formatted) || !value
                                ? []
                                : [setFieldValue({ fieldId: `${FieldIds.Quantity}-${index}`, value: formatted })];
                        },
                    },
                }),
            ],
        };
    };

// Channel installation type configuration (Series 500)
export const channelInstallationConfig = (state: UnknownLocationCardState, isMp1: boolean): Section => {
    const settings = state.settings;
    let details: LocationDetails;

    if (state.locationDetails && state.locationDetails.length > 0) {
        details = isMp1 ? state.locationDetails[0] : state.locationDetails[1];
    }

    const FieldIds = isMp1 ? ChannelInstallationFieldIds : ChannelInstallationMp2FieldIds;
    return {
        info: {
            id: InstallationTypeSectionIds.Channel,
            title: '',
            displayTitle: false,
        },
        rows: [
            // Range/Height Row
            {
                info: {
                    id: 'channel-range-row',
                    panelClass: 'full-width',
                },
                items: [
                    field.number(FieldIds.Range, 'range', {
                        value: details ? details.range : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.HEIGHT',
                        placeholderSuffix: settings.depthUnit,
                        validators: getValidators(settings.isMetric),
                        panelClass: 'pad-t-6 full-width required',
                    }),
                ],
            },

            // Number of Entries Row
            {
                info: {
                    id: 'channel-num-entries-row',
                    panelClass: 'full-width',
                },
                items: [
                    field.number(FieldIds.NumberOfEntries, 'numberOfEntries', {
                        value: (details && details.entries) ? details.entries.length : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_ENTRIES_LABEL',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.channelEntriesLimit,
                                message: 'HOME.MAP.MARKER_LOCATION_DETAIL.CHANNEL_ENTRY_ERROR',
                            }),
                            min({ value: 2 }),
                            max({ value: 257 }),
                        ],
                        panelClass: 'pad-t-6 full-width required',
                        events: {
                            onChange: (value) =>
                                value < 2 || value > 257
                                    ? []
                                    : [
                                          setArraySectionItems({
                                              fieldId: FieldIds.ArraySection,
                                              newItemsCount: Number(value),
                                          }),
                                      ],
                        },
                    }),
                ],
            },

            // Entries Array Container Row
            {
                info: {
                    id: 'channel-entries-container-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: FieldIds.EntriesSection,
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: FieldIds.ArraySection,
                                title: '',
                                displayTitle: false,
                                displayActionBtns: false,
                                type: SectionType.Array,
                                expanded: true,
                                expandable: false,
                                itemGenerator: channelRowGenerator(state, isMp1),
                                panelClass: 'channel-entries-panel',
                            },
                            rows: (details && details.entries) ? details.entries.map((v, i) => channelRowGenerator(state, isMp1)(i, v)) : [],
                        },
                    },
                ],
            },
        ],
    };
};
